/**
 * v3.21-1813 · Last Communication summary strip.
 *
 * Renders the freshest canonical communication for the Client in a
 * compact card, directly beneath the primary Client action row.
 * Payload comes from GET /api/clients/{id}/communication-summary
 * (see backend/routes/communication_awareness.py).  Content is kept
 * intentionally small — NO email bodies, NO transcripts, NO
 * financial data.  Deep-link CTA opens the Communication History
 * tab via the same tab controller the shell already exposes.
 *
 * Accessibility: NEW status never encoded by color alone.  The
 * component surfaces the NEW count as text (e.g. "3 NEW") and uses
 * a filled Badge so screen readers announce it explicitly.
 */
import React from 'react';
import { Phone, Mail, MessageSquare, Voicemail, ClipboardList, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const CHANNEL_META = {
  call:       { Icon: Phone,          label: 'Call',        wrap: 'bg-orange-50 text-orange-800 border-orange-200' },
  email:      { Icon: Mail,           label: 'Email',       wrap: 'bg-blue-50 text-blue-800 border-blue-200' },
  sms:        { Icon: MessageSquare,  label: 'SMS',         wrap: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  voicemail:  { Icon: Voicemail,      label: 'Voicemail',   wrap: 'bg-amber-50 text-amber-800 border-amber-200' },
  manual:     { Icon: ClipboardList,  label: 'Note',        wrap: 'bg-slate-50 text-slate-700 border-slate-200' },
  communication: { Icon: ClipboardList, label: 'Communication', wrap: 'bg-slate-50 text-slate-700 border-slate-200' },
};

function fmtWhen(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (sameDay) return `Today ${time}`;
    const yest = new Date(Date.now() - 86400000);
    if (d.toDateString() === yest.toDateString()) return `Yesterday ${time}`;
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
  } catch (_e) {
    return String(ts);
  }
}

const LastCommunicationStrip = ({ summary, onOpenHistory }) => {
  const total = summary?.total_count ?? 0;
  const unread = summary?.unread_count ?? 0;
  const last = summary?.last_communication;

  if (!summary || total === 0) {
    return (
      <div
        data-testid="last-communication-strip"
        data-empty="true"
        className="mb-3 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 flex items-center justify-between text-xs text-slate-500"
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="h-3.5 w-3.5" />
          <span className="uppercase tracking-wide text-[10px] text-slate-500">Last Communication</span>
          <span>No communications recorded yet.</span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 text-xs text-slate-500"
          onClick={onOpenHistory}
          data-testid="last-communication-open-history-empty"
        >
          Open Communication History <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    );
  }

  const meta = CHANNEL_META[last?.channel] || CHANNEL_META.communication;
  const { Icon, label, wrap } = meta;
  const directionText = last?.direction
    ? last.direction[0].toUpperCase() + last.direction.slice(1)
    : '';
  const author = last?.user_name || '';
  const disposition = last?.disposition || '';
  const preview = last?.preview || last?.subject || '';

  return (
    <div
      data-testid="last-communication-strip"
      data-total-count={total}
      data-unread-count={unread}
      className="mb-3 rounded-md border border-slate-200 bg-white shadow-sm"
    >
      <div className="flex items-start gap-3 px-3 py-2.5">
        <div className={`mt-0.5 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium ${wrap}`}>
          <Icon className="h-3.5 w-3.5" />
          <span>{label}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
            <span className="uppercase tracking-wide text-[10px] font-semibold text-slate-500">Last Communication</span>
            <span>· {fmtWhen(last?.timestamp)}</span>
            {directionText && <span>· {directionText}</span>}
            {author && <span>· {author}</span>}
            {disposition && <span className="text-slate-600">· {disposition}</span>}
          </div>
          {preview && (
            <p
              className="mt-0.5 text-xs text-slate-700 truncate"
              data-testid="last-communication-preview"
              title={preview}
            >
              {preview}
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            <Badge
              variant="outline"
              className="text-[10px] font-normal border-slate-200 text-slate-600"
              data-testid="last-communication-total-badge"
            >
              {total} total
            </Badge>
            {unread > 0 && (
              <Badge
                className="text-[10px] bg-red-600 hover:bg-red-600 text-white border-transparent"
                data-testid="last-communication-new-badge"
                aria-label={`${unread} new communication${unread === 1 ? '' : 's'}`}
              >
                {unread} NEW
              </Badge>
            )}
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs shrink-0"
          onClick={onOpenHistory}
          data-testid="last-communication-open-history"
          title="View Full Communication History"
        >
          View Full Communication History
          <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default LastCommunicationStrip;
